/**
 * Formatting for the admin console: Czech formats in both languages (the
 * design system's rule), relative times, money in Kč.
 */
export type Lang = "cs" | "en";

export function makeTr(language: string) {
  return (cs: string, en: string) => (language === "en" ? en : cs);
}

export function formatCzk(amount: number): string {
  return new Intl.NumberFormat("cs-CZ", {
    style: "currency",
    currency: "CZK",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat("cs-CZ").format(n);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("cs-CZ", { timeZone: "Europe/Prague" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("cs-CZ", {
    timeZone: "Europe/Prague",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "před 5 min" / "5 min ago"; future instants read "za 3 d" / "in 3 d". */
export function timeAgo(
  iso: string | null | undefined,
  language: string,
  now = Date.now()
): string {
  const tr = makeTr(language);
  if (!iso) return tr("nikdy", "never");
  const diff = now - Date.parse(iso);
  const abs = Math.abs(diff);
  const unit =
    abs < 60_000
      ? null
      : abs < 3_600_000
        ? `${Math.round(abs / 60_000)} min`
        : abs < 172_800_000
          ? `${Math.round(abs / 3_600_000)} h`
          : `${Math.round(abs / 86_400_000)} d`;
  if (!unit) return tr("právě teď", "just now");
  return diff >= 0 ? tr(`před ${unit}`, `${unit} ago`) : tr(`za ${unit}`, `in ${unit}`);
}

/** Days from now to `iso`, rounded up; negative once it has passed. */
export function daysUntil(iso: string, now = Date.now()): number {
  return Math.ceil((Date.parse(iso) - now) / 86_400_000);
}

export function roleLabel(role: string, language: string): string {
  const tr = makeTr(language);
  if (role === "owner") return tr("vlastník", "owner");
  if (role === "admin") return tr("správce", "admin");
  if (role === "member") return tr("člen", "member");
  return role;
}
